import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Code2, Eye, Search, Send, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { KbRichEditor } from "@/components/KbRichEditor";
import { KbSectionsEditor } from "@/components/KbSectionsEditor";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { KB_AUDIENCES } from "@/lib/kbAudiences";
import { KbSnippetInfo } from "@/components/KbSnippetInfo";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";
import { useAuth } from "@/context/AuthContext";

const deriveAudiences = (record) =>
  record?.audiences?.length ? record.audiences : (record?.scope === "internal" ? ["internal"] : ["everyone"]);

/**
 * KB editor. One dialog for articles and categories; `kind` switches the field
 * set. `mode="create"` authors a brand-new article/snippet (draft→publish);
 * `mode="edit"` patches an existing record. Visibility = audiences (default) +
 * optional per-team / per-organization targeting.
 */
export function KbEditDialog({
  open, onOpenChange, kind, record, mode = "edit", snippet = false,
  tree = [], tableCount = 0, onSaved, onDeleted,
}) {
  const isArticle = kind === "article";
  const isCreate = mode === "create";
  const [form, setForm] = useState(() => ({
    name: record?.name || "",
    description: record?.description || "",
    icon: record?.icon || "",
    audiences: deriveAudiences(record),
    target_teams: record?.target_teams || [],
    target_orgs: record?.target_orgs || [],
    keywords: record?.keywords || [],
    body_html: record?.body_html || "",
    table_styles: record?.table_styles || {},
    sections: record?.sections || [],
    primary_category_id: record?.primary_category_id || "all",
    new_state: record?.new_override === true ? "on" : record?.new_override === false ? "off" : "auto",
    reviewer_id: record?.reviewer_id || "",
  }));
  const [options, setOptions] = useState({ teams: [], organizations: [] });
  const [orgFilter, setOrgFilter] = useState("");
  const [kwDraft, setKwDraft] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/kb/visibility/options")
      .then((r) => setOptions({ teams: r.data.teams || [], organizations: r.data.organizations || [] }))
      .catch(() => {});
  }, [open]);

  const toggleIn = (key, val) => setForm((f) => {
    const cur = new Set(f[key] || []);
    cur.has(val) ? cur.delete(val) : cur.add(val);
    return { ...f, [key]: [...cur] };
  });

  const toggleAudience = (val) => setForm((f) => {
    const cur = new Set(f.audiences || []);
    if (cur.has(val)) { cur.delete(val); return { ...f, audiences: [...cur] }; }
    if (val === "everyone") return { ...f, audiences: ["everyone"] };
    cur.delete("everyone");
    cur.add(val);
    return { ...f, audiences: [...cur] };
  });

  const addKeyword = () => {
    const k = kwDraft.trim().toLowerCase();
    if (!k) return;
    setForm((f) => ({ ...f, keywords: [...new Set([...(f.keywords || []), k])].slice(0, 40) }));
    setKwDraft("");
  };
  const removeKeyword = (k) => setForm((f) => ({ ...f, keywords: (f.keywords || []).filter((x) => x !== k) }));

  const save = async (statusOverride) => {
    if (isArticle && !form.reviewer_id && (!isAdmin || statusOverride === "in_review")) {
      toast.error(statusOverride === "in_review" ? "Pick a reviewer to submit it for review" : "Pick a reviewer — every article and snippet needs one");
      return;
    }
    setSaving(true);
    const status = statusOverride || "draft";
    try {
      if (isCreate && isArticle) {
        const payload = {
          name: form.name, description: form.description, body_html: form.body_html,
          audiences: form.audiences, target_teams: form.target_teams, target_orgs: form.target_orgs,
          keywords: form.keywords, is_snippet: snippet, status, reviewer_id: form.reviewer_id || undefined,
          category_ids: form.primary_category_id !== "all" ? [form.primary_category_id] : [],
          primary_category_id: form.primary_category_id !== "all" ? form.primary_category_id : null,
        };
        const { data } = await api.post("/kb/articles", payload);
        toast.success(status === "in_review" ? "Submitted — the reviewer has been notified"
          : status === "published" ? "Published — it's live now" : "Draft saved");
        onOpenChange(false);
        onSaved?.(data);
        return;
      }
      const url = isArticle ? `/kb/articles/${record.slug}` : `/kb/categories/${record.slug}`;
      const payload = isArticle
        ? { name: form.name, description: form.description, audiences: form.audiences,
            target_teams: form.target_teams, target_orgs: form.target_orgs, reviewer_id: form.reviewer_id || undefined,
            keywords: form.keywords, body_html: form.body_html, table_styles: form.table_styles,
            sections: form.sections, new_state: form.new_state,
            ...(statusOverride === "published" ? { status: "published" } : {}) }
        : { name: form.name, description: form.description, icon: form.icon, audiences: form.audiences,
            target_teams: form.target_teams, target_orgs: form.target_orgs, new_state: form.new_state };
      const { data } = await api.patch(url, payload);
      if (isArticle && statusOverride === "in_review") {
        await api.post(`/kb/articles/${record.slug}/review/request`, { reviewer_id: form.reviewer_id });
        toast.success("Submitted — the reviewer has been notified");
      } else if (isArticle && statusOverride === "published") {
        toast.success("Published — this version is live now");
      } else if (isArticle && data.pending) {
        toast.success("Saved — your update waits for review; readers still see the published version");
      } else {
        toast.success(isArticle ? (snippetMode ? "Snippet updated" : "Article updated") : "Category updated");
      }
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${record.name}”? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/kb/articles/${record.slug}`);
      toast.success("Article deleted");
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setDeleting(false); }
  };

  const toggleTable = (i, v) => setForm((f) => ({
    ...f, table_styles: { ...f.table_styles, [i]: { borderless: v } },
  }));

  const snippetMode = snippet || !!record?.is_snippet;
  const title = isCreate
    ? (snippetMode ? "New snippet" : "New article")
    : (isArticle ? (snippetMode ? "Edit snippet" : "Edit article") : "Edit category");
  const filteredOrgs = options.organizations.filter(
    (o) => !orgFilter || (o.name || "").toLowerCase().includes(orgFilter.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`flex max-h-[90vh] flex-col gap-0 p-0 ${isArticle ? "max-w-4xl" : "max-w-2xl"}`} data-testid="kb-edit-dialog">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            {title}
            {isArticle && snippetMode && <KbSnippetInfo />}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4" data-testid="kb-edit-scroll">
          <div className="space-y-1.5">
            <Label>{isArticle ? "Title" : "Name"}</Label>
            <Input data-testid="kb-edit-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input data-testid="kb-edit-description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          {!isArticle && (
            <div className="space-y-1.5">
              <Label>Icon <span className="text-xs font-normal text-slate-400 dark:text-slate-500">(FontAwesome class from the old KB)</span></Label>
              <Input data-testid="kb-edit-icon" value={form.icon} placeholder="far fa-hotel"
                onChange={(e) => setForm({ ...form, icon: e.target.value })} />
            </div>
          )}

          {isCreate && isArticle && !snippet && (
            <div className="space-y-1.5">
              <Label>Category</Label>
              <KbCategoryFilter tree={tree} value={form.primary_category_id} single
                allLabel="Uncategorised" testid="kb-edit-category"
                onChange={(id) => setForm((f) => ({ ...f, primary_category_id: id }))} />
              <p className="text-xs text-slate-400 dark:text-slate-500">Where this article lives in the browse tree.</p>
            </div>
          )}

          {/* Reviewer (required) + where this stands */}
          {isArticle && (
            <div className="space-y-1.5" data-testid="kb-edit-review">
              <Label>Reviewer <span className="text-amber-600">*</span></Label>
              <KbReviewerSelect value={form.reviewer_id} onChange={(v) => setForm({ ...form, reviewer_id: v })}
                testid="kb-edit-reviewer" />
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {isCreate
                  ? "Nothing goes public until the reviewer approves it. You can't review your own work."
                  : record?.is_draft
                    ? (record?.review_state === "in_review" ? `In review with ${record.reviewer_name || "the reviewer"} — still hidden from readers.` : "Draft — hidden from readers until the reviewer approves and publishes it.")
                    : "Published. Your content edits wait for the reviewer; readers keep seeing the current version until approved."}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Visibility — audiences</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="kb-edit-audiences">
              {KB_AUDIENCES.map((a) => {
                const on = (form.audiences || []).includes(a.value);
                return (
                  <button key={a.value} type="button" data-testid={`kb-edit-aud-${a.value}`}
                    onClick={() => toggleAudience(a.value)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      on ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    {a.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Who can see this {isArticle ? "article" : "category"}. Staff see every audience — use the teams below to narrow them.
            </p>
            {(!form.audiences || !form.audiences.length) && (
              <p className="text-xs text-amber-600">No audience selected — only staff will see this.</p>
            )}
          </div>

          {/* Optional per-team / per-org narrowing */}
          <div className="space-y-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid="kb-edit-targeting">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Narrow further (optional)</div>
            <p className="-mt-1 text-xs text-slate-500 dark:text-slate-400">
              Leave empty for no extra limit. When set, a viewer must also match one of the chosen
              teams/organizations — staff included. Only super admins see past this; editors still
              reach it from Manage articles.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Teams</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="kb-edit-teams">
                {options.teams.length === 0 && <span className="text-xs text-slate-400 dark:text-slate-500">No teams found</span>}
                {/* Teams from Settings → Admin → Teams, plus any legacy value this item still targets so it can be removed. */}
                {[...options.teams, ...(form.target_teams || []).filter((t) => !options.teams.includes(t))].map((t) => {
                  const on = (form.target_teams || []).includes(t);
                  return (
                    <button key={t} type="button" data-testid={`kb-edit-team-${t}`}
                      onClick={() => toggleIn("target_teams", t)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        on ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Organizations {form.target_orgs?.length ? `(${form.target_orgs.length} selected)` : ""}</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input data-testid="kb-edit-org-filter" value={orgFilter} placeholder="Filter organizations…"
                  onChange={(e) => setOrgFilter(e.target.value)} className="h-8 pl-8 text-xs" />
              </div>
              <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-slate-100 dark:border-slate-800 p-1" data-testid="kb-edit-orgs">
                {filteredOrgs.slice(0, 100).map((o) => {
                  const on = (form.target_orgs || []).includes(o.org_id);
                  return (
                    <button key={o.org_id} type="button" data-testid={`kb-edit-org-${o.org_id}`}
                      onClick={() => toggleIn("target_orgs", o.org_id)}
                      className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs transition-colors ${
                        on ? "bg-indigo-50 text-indigo-700" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                      <span className="truncate">{o.name}</span>
                      {on && <span className="ml-2 text-[10px] font-semibold">✓</span>}
                    </button>
                  );
                })}
                {filteredOrgs.length === 0 && <div className="px-2 py-1 text-xs text-slate-400 dark:text-slate-500">No matching organizations</div>}
              </div>
            </div>
          </div>

          {isArticle && (
            <div className="space-y-1.5 rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid="kb-edit-keywords">
              <Label className="text-xs">Search keywords <span className="font-normal text-slate-400 dark:text-slate-500">(invisible to readers — improves search only)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {(form.keywords || []).map((k) => (
                  <span key={k} data-testid={`kb-keyword-${k}`}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-200">
                    {k}
                    <button type="button" onClick={() => removeKeyword(k)} className="text-slate-400 dark:text-slate-500 hover:text-rose-500">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="kb-keyword-input" value={kwDraft} placeholder="Add a keyword and press Enter"
                  onChange={(e) => setKwDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                  className="h-8 text-xs" />
                <Button type="button" variant="outline" size="sm" data-testid="kb-keyword-add" onClick={addKeyword}>Add</Button>
              </div>
            </div>
          )}

          {!isCreate && (
            <div className="space-y-1.5">
              <Label>&ldquo;New&rdquo; badge</Label>
              <div className="flex gap-1.5" data-testid="kb-edit-new-toggle">
                {[["auto", "Auto (90 days)"], ["on", "Always show"], ["off", "Hide"]].map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`kb-edit-new-${v}`}
                    onClick={() => setForm({ ...form, new_state: v })}
                    className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                      form.new_state === v ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isArticle && !isCreate && tableCount > 0 && (
            <div className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid="kb-edit-tables">
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Table borders</div>
              {Array.from({ length: tableCount }).map((_, i) => (
                <div key={i} className="flex items-center justify-between rounded border border-slate-100 dark:border-slate-800 px-3 py-2">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Table {i + 1}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">Borderless</span>
                    <Switch data-testid={`kb-edit-table-borderless-${i}`}
                      checked={!!form.table_styles?.[i]?.borderless}
                      onCheckedChange={(v) => toggleTable(i, v)} />
                  </span>
                </div>
              ))}
            </div>
          )}

          {isArticle && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Content</Label>
                <button type="button" data-testid="kb-edit-html-toggle"
                  onClick={() => setHtmlMode((m) => !m)}
                  className="flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                  {htmlMode ? <><Eye className="h-3.5 w-3.5" /> Visual editor</> : <><Code2 className="h-3.5 w-3.5" /> HTML source</>}
                </button>
              </div>
              {htmlMode ? (
                <textarea data-testid="kb-edit-body"
                  className="min-h-[70vh] w-full rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 font-mono text-xs shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  value={form.body_html}
                  onChange={(e) => setForm({ ...form, body_html: e.target.value })} />
              ) : (
                <KbRichEditor value={form.body_html} tall
                  onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} />
              )}
            </div>
          )}

          {isArticle && !isCreate && (
            <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
              <KbSectionsEditor sections={form.sections} baseHtml={form.body_html}
                onChange={(sections) => setForm((f) => ({ ...f, sections }))} />
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-border px-6 py-4 sm:justify-between">
          {isArticle && !isCreate ? (
            <Button variant="outline" onClick={remove} disabled={deleting} data-testid="kb-edit-delete"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
              {deleting ? "Deleting…" : "Delete article"}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {isCreate && isArticle ? (
              <>
                <Button variant="outline" data-testid="kb-create-draft" onClick={() => save("draft")} disabled={saving || !form.name.trim()}>
                  {saving ? "Saving…" : "Save as draft"}
                </Button>
                <Button data-testid="kb-create-submit" onClick={() => save("in_review")} disabled={saving || !form.name.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Send className="mr-1 h-4 w-4" /> {saving ? "Submitting…" : "Submit for review"}
                </Button>
                {isAdmin && (
                  <Button variant="outline" data-testid="kb-create-publish-now" onClick={() => save("published")}
                    disabled={saving || !form.name.trim()} title="Super admin: publish without waiting for the reviewer"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300">
                    <ShieldCheck className="mr-1 h-4 w-4" /> Publish now
                  </Button>
                )}
              </>
            ) : isArticle ? (
              <>
                <Button variant="outline" data-testid="kb-edit-save" onClick={() => save()} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button data-testid="kb-edit-submit" onClick={() => save("in_review")} disabled={saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Send className="mr-1 h-4 w-4" /> {saving ? "Submitting…" : "Submit for review"}
                </Button>
                {isAdmin && (
                  <Button variant="outline" data-testid="kb-edit-publish-now" onClick={() => save("published")}
                    disabled={saving} title="Super admin: publish without waiting for the reviewer"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300">
                    <ShieldCheck className="mr-1 h-4 w-4" /> {record?.is_draft === false ? "Publish update now" : "Publish now"}
                  </Button>
                )}
              </>
            ) : (
              <Button data-testid="kb-edit-save" onClick={() => save()} disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
